import { defineComponent } from 'vue'
import Container from 'typedi'
import Notification from '@/modules/common/services/notification.service'
import { PrintQueueItem } from '@/modules/level-two/models'

// How long a fully successful job's result stays visible before the modal closes itself
const AUTO_CLOSE_DELAY_MS = 2000

export default defineComponent({
  name: 'BadgePrintQueueMixin',

  data () {
    return {
      autoCloseTimer: null as ReturnType<typeof setTimeout> | null,
      sortFields: [] as string[],
      sortDesc: false,
      printing: false,
      printLoopActive: false, // true from when a print loop starts until its in-flight item settles, even across a pause
      haltMessage: '',
      pausePrinting: false,
      printed: 0,
      printedItems: [] as PrintQueueItem[],
      errors: 0,
      currentParticipantName: '',
      expandedDrill: null as string | null
    }
  },

  computed: {
    printingIndex (): number {
      return this.printed + this.errors
    },

    remainingToPrint (): PrintQueueItem[] {
      return this.itemsToPrint.slice(this.printingIndex)
    },

    jobStarted (): boolean {
      return this.printing || this.pausePrinting || this.printingIndex > 0
    },

    jobDone (): boolean {
      return this.jobStarted && !this.printing && !this.pausePrinting && this.printingIndex >= this.itemsToPrint.length
    },

    canDrillDown (): boolean {
      return this.itemsToPrint.length > 1
    },

    sortableFields (): string[] {
      if (!(this as any).printQueue.length) return []
      return Object.keys((this as any).printQueue[0]).filter((field: string) => field !== 'content' && field !== 'participant_uuid')
    },

    itemsToPrint (): PrintQueueItem[] {
      const printQueue = (this as any).printQueue as PrintQueueItem[]
      if (this.sortFields.length === 0) {
        return printQueue
      }

      return [...printQueue].sort((a: PrintQueueItem, b: PrintQueueItem) => {
        for (const field of this.sortFields) {
          const aVal = a[field]?.toString() ?? ''
          const bVal = b[field]?.toString() ?? ''

          const result = aVal.localeCompare(bVal, undefined, { numeric: true })
          if (result) {
            return this.sortDesc ? -result : result
          }
        }
        return 0
      })
    }
  },

  beforeDestroy () {
    (this as any).cancelAutoClose()
  },

  methods: {
    /**
     * Close the host's modal shortly after a job succeeds, leaving the result visible for a moment first.
     * Failed jobs never call this, so their modal stays open and shows the failure.
     * @param {() => void} close
     */
    scheduleAutoClose (close: () => void) {
      this.cancelAutoClose()
      this.autoCloseTimer = setTimeout(() => {
        this.autoCloseTimer = null
        close()
      }, AUTO_CLOSE_DELAY_MS)
    },

    cancelAutoClose () {
      if (this.autoCloseTimer) {
        clearTimeout(this.autoCloseTimer)
        this.autoCloseTimer = null
      }
    },

    /**
     * Reset print progress so reopening/restarting a job never shows a stale completed job
     */
    resetPrintProgress () {
      this.cancelAutoClose()
      this.printed = 0
      this.errors = 0
      this.printedItems = []
      this.pausePrinting = false
      this.haltMessage = ''
      this.expandedDrill = null
    },

    /**
     * Print entire queue
     */
    printAll () {
      // Resetting the counters under a loop that is still settling its in-flight item would corrupt them
      if (this.printLoopActive) return

      this.resetPrintProgress()
      this.print()
    },

    /**
     * Loop through and print content
     */
    async print () {
      // A paused loop is still settling its in-flight item, so printing is false but the loop is not finished.
      // Starting a second loop now would send that item to the printer twice.
      if (this.printLoopActive) return

      this.printLoopActive = true
      this.pausePrinting = false
      this.haltMessage = ''
      this.printing = true

      try {
        for (let i = this.printingIndex; i < this.itemsToPrint.length; i++) {
          if (this.pausePrinting) {
            Container.get(Notification).info('Printing paused')
            break
          }

          const item = this.itemsToPrint[i]
          const participantName = this.getParticipantName(item)
          this.currentParticipantName = participantName

          const result = await (this as any).printItem(item)

          if (result.success) {
            this.printed++
            this.printedItems.push(item)
          } else if (result.stage === 'fetch') {
            Container.get(Notification).error('Error fetching badge for ' + participantName)
            this.errors++
            this.haltMessage = `Printing stopped: the badge for ${participantName} could not be loaded.`
            break // stop the loop since there is probably a systemic problem here
          } else if (result.stage === 'none') {
            this.errors++
            Container.get(Notification).error(`Print error for ${participantName}: nothing to print`)
          } else if (result.message) {
            this.errors++
            Container.get(Notification).error(`Print error for ${participantName}: ${result.message}`)
          } else {
            this.errors++
            Container.get(Notification).error('Error printing badge for ' + participantName)
            // Continue the loop even though this print job failed. The user can cancel if they want to.
          }
        }
      } finally {
        this.printing = false
        this.printLoopActive = false
      }
    },

    /**
     * Pause printing on the next item
     */
    pause () {
      this.pausePrinting = true
      this.printing = false
    },

    /**
     * Get a participant's display name
     * @param {PrintQueueItem} item
     * @returns {string}
     */
    getParticipantName (item: PrintQueueItem): string {
      return (item.full_name as string) ?? ((item.first_name ? item.first_name + ' ' : '') + (item.last_name ?? ''))
    },

    /**
     * Get a participant's initials for the drill-down list avatar
     * @param {PrintQueueItem} item
     * @returns {string}
     */
    getInitials (item: PrintQueueItem): string {
      const parts = this.getParticipantName(item).trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return ''
      const initials = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0][0]
      return initials.toUpperCase()
    },

    /**
     * Toggle the drill-down list for a stat box, closing any other open list
     * @param {string} id
     */
    toggleDrill (id: string) {
      this.expandedDrill = this.expandedDrill === id ? null : id
    }
  }
})
